import React, { useEffect, useState, useCallback } from 'react';
import { tenantService } from '../../services/superAdminService';
import './Tenants.css';

interface Tenant {
  _id: string;
  name: string;
  code: string;
  description?: string;
  address?: string;
  contactEmail?: string;
  contactPhone?: string;
  status: string;
  subscription: {
    plan: string;
    maxShops: number;
    maxStaff: number;
    startDate: string;
    endDate: string;
  };
  shopCount: number;
  staffCount: number;
  adminCount: number;
  createdAt: string;
}

const Tenants: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [formData, setFormData] = useState({
    tenantName: '',
    tenantCode: '',
    description: '',
    address: '',
    contactEmail: '',
    contactPhone: '',
    adminName: '',
    adminMobile: '',
    adminPassword: '',
    adminEmail: '',
    plan: 'basic',
    maxShops: 100,
    maxStaff: 50,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadTenants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await tenantService.getAll({
        status: statusFilter || undefined,
        search: searchTerm || undefined,
      });
      if (response.data?.success) {
        setTenants(response.data.data.tenants);
      }
    } catch (error) {
      console.error('Error loading tenants:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await tenantService.create({
        ...formData,
        maxShops: Number(formData.maxShops),
        maxStaff: Number(formData.maxStaff),
      });

      if (response.data?.success) {
        setShowModal(false);
        setFormData({
          tenantName: '',
          tenantCode: '',
          description: '',
          address: '',
          contactEmail: '',
          contactPhone: '',
          adminName: '',
          adminMobile: '',
          adminPassword: '',
          adminEmail: '',
          plan: 'basic',
          maxShops: 100,
          maxStaff: 50,
        });
        loadTenants();
      } else {
        setError(response.data?.message || 'Failed to create tenant');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (tenantId: string, newStatus: string) => {
    try {
      await tenantService.update(tenantId, { status: newStatus });
      loadTenants();
    } catch (error) {
      console.error('Error updating tenant status:', error);
    }
  };

  const viewTenantDetails = async (tenant: Tenant) => {
    try {
      const response = await tenantService.getById(tenant._id);
      if (response.data?.success) {
        setSelectedTenant({ ...tenant, ...response.data.data.tenant });
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Error loading tenant details:', error);
    }
  };

  return (
    <div className="tenants-page">
      <div className="page-header">
        <div>
          <h1>Tenant Management</h1>
          <p>Manage all organizations and their admins</p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          + Add Tenant
        </button>
      </div>

      {/* Filters */}
      <div className="filters">
        <input
          type="text"
          placeholder="Search by name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {/* Tenants Table */}
      <div className="table-wrapper">
        {loading ? (
          <div className="loading">Loading tenants...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Admins</th>
                <th>Shops</th>
                <th>Staff</th>
                <th>Subscription End</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant._id}>
                  <td><strong>{tenant.code}</strong></td>
                  <td>{tenant.name}</td>
                  <td>
                    <span className={`plan-badge plan-${tenant.subscription?.plan}`}>
                      {tenant.subscription?.plan || 'basic'}
                    </span>
                  </td>
                  <td>
                    <select
                      value={tenant.status}
                      onChange={(e) => handleStatusChange(tenant._id, e.target.value)}
                      className={`status-select status-${tenant.status}`}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </td>
                  <td>{tenant.adminCount}</td>
                  <td>{tenant.shopCount} / {tenant.subscription?.maxShops || '-'}</td>
                  <td>{tenant.staffCount} / {tenant.subscription?.maxStaff || '-'}</td>
                  <td>
                    {tenant.subscription?.endDate
                      ? new Date(tenant.subscription.endDate).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <button
                      className="btn-view"
                      onClick={() => viewTenantDetails(tenant)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && (
                <tr>
                  <td colSpan={9} className="no-data">No tenants found</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Tenant Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Tenant</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              {error && <div className="error-message">{error}</div>}
              
              <div className="form-section">
                <h3>Organization Details</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Organization Name *</label>
                    <input
                      type="text"
                      name="tenantName"
                      value={formData.tenantName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Organization Code *</label>
                    <input
                      type="text"
                      name="tenantCode"
                      value={formData.tenantCode}
                      onChange={handleChange}
                      placeholder="e.g., EDGE_VYAPARI"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={2}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Address</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Contact Phone</label>
                    <input
                      type="text"
                      name="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Contact Email</label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Admin Account</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Admin Name *</label>
                    <input
                      type="text"
                      name="adminName"
                      value={formData.adminName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Admin Mobile *</label>
                    <input
                      type="text"
                      name="adminMobile"
                      value={formData.adminMobile}
                      onChange={handleChange}
                      pattern="[0-9]{10}"
                      title="10 digit mobile number"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Admin Password *</label>
                    <input
                      type="password"
                      name="adminPassword"
                      value={formData.adminPassword}
                      onChange={handleChange}
                      minLength={6}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Admin Email</label>
                    <input
                      type="email"
                      name="adminEmail"
                      value={formData.adminEmail}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Subscription</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Plan</label>
                    <select name="plan" value={formData.plan} onChange={handleChange}>
                      <option value="basic">Basic</option>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Max Shops</label>
                    <input
                      type="number"
                      name="maxShops"
                      value={formData.maxShops}
                      onChange={handleChange}
                      min={1}
                    />
                  </div>
                  <div className="form-group">
                    <label>Max Staff</label>
                    <input
                      type="number"
                      name="maxStaff"
                      value={formData.maxStaff}
                      onChange={handleChange}
                      min={1}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Tenant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tenant Detail Modal */}
      {showDetailModal && selectedTenant && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedTenant.name}</h2>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>&times;</button>
            </div>
            <div className="tenant-detail">
              <div className="detail-section">
                <h3>Organization Info</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Code</label>
                    <span>{selectedTenant.code}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    <span className={`status-badge status-${selectedTenant.status}`}>
                      {selectedTenant.status}
                    </span>
                  </div>
                  {selectedTenant.description && (
                    <div className="detail-item full-width">
                      <label>Description</label>
                      <span>{selectedTenant.description}</span>
                    </div>
                  )}
                  {selectedTenant.address && (
                    <div className="detail-item full-width">
                      <label>Address</label>
                      <span>{selectedTenant.address}</span>
                    </div>
                  )}
                  {selectedTenant.contactEmail && (
                    <div className="detail-item">
                      <label>Email</label>
                      <span>{selectedTenant.contactEmail}</span>
                    </div>
                  )}
                  {selectedTenant.contactPhone && (
                    <div className="detail-item">
                      <label>Phone</label>
                      <span>{selectedTenant.contactPhone}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h3>Subscription</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Plan</label>
                    <span className={`plan-badge plan-${selectedTenant.subscription?.plan}`}>
                      {selectedTenant.subscription?.plan || 'basic'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Max Shops</label>
                    <span>{selectedTenant.subscription?.maxShops}</span>
                  </div>
                  <div className="detail-item">
                    <label>Max Staff</label>
                    <span>{selectedTenant.subscription?.maxStaff}</span>
                  </div>
                  <div className="detail-item">
                    <label>Expires</label>
                    <span>
                      {selectedTenant.subscription?.endDate
                        ? new Date(selectedTenant.subscription.endDate).toLocaleDateString()
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Statistics</h3>
                <div className="stats-row">
                  <div className="stat-item">
                    <span className="stat-value">{selectedTenant.adminCount}</span>
                    <span className="stat-label">Admins</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{selectedTenant.shopCount}</span>
                    <span className="stat-label">Shops</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{selectedTenant.staffCount}</span>
                    <span className="stat-label">Staff</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tenants;
